import type { CancelRequest } from "./lineage";

// Docit Work 23 — the To-do dashboard's wire (`work.todo`) and its pure rules: kind order, sorting (impact →
// urgency → age), how an action's text lands in its call(s), the optimistic removal after an action, and what an
// action unblocked. The shapes are the server's (WorkTodoController); nothing here fetches.

export type TodoKind =
  | "acceptance"
  | "question"
  | "failedStep"
  | "stop"
  | "flowStalled"
  | "planApproval"
  | "personStep"
  | "mailTodo"
  | "channelDecision"
  | "queuedNoWindow"
  | "manualBlock"
  | "leftover"
  | "dependencyOnOthers";

/** The server's tie-break order (after impact and urgency). */
export const TODO_KINDS: TodoKind[] = [
  "acceptance",
  "question",
  "failedStep",
  "stop",
  "flowStalled",
  "planApproval",
  "personStep",
  "mailTodo",
  "channelDecision",
  "queuedNoWindow",
  "manualBlock",
  "leftover",
  "dependencyOnOthers",
];

export interface TodoCall {
  method: "POST";
  /** `/api/work.…` — an existing endpoint. */
  path: string;
  body: Record<string, unknown>;
  /** Dotted path in `body` where the person's text goes (`answer.text`, `summary`, `reason`…). */
  textPath?: string;
}

export interface TodoAction {
  id: string;
  /** call: POST `call`, then each `andThen` · preview-apply: `call` is the dry run, `apply` the real one · open: no call. */
  kind: "call" | "preview-apply" | "open";
  label: string;
  /** What pressing it does — shown under the button. */
  description: string;
  requiresText: boolean;
  /** A text box is shown (optional unless `requiresText`). */
  acceptsText?: boolean;
  danger: boolean;
  primary?: boolean;
  call: TodoCall | null;
  andThen?: TodoCall[];
  apply?: TodoCall;
  /** preview-apply: what the desk's CancelDialog needs. */
  cancel?: CancelRequest;
  open?: {
    url?: string | null;
    repoPath?: string | null;
    machine?: string | null;
    label?: string | null;
    teamId?: string | null;
  };
}

export interface TodoChainLink {
  key: string;
  title: string;
  state: string;
  blocked: boolean;
  why: string | null;
  /** Why it waits, said with names (server WorkTodoWhy, 2026-10-16) — `why` is the engine's raw mark. */
  reason?: string | null;
}

/** What stopped a card, in words, and the run behind it (server WorkTodoWhy, 2026-10-16). */
export interface TodoWhyInfo {
  cause: string | null;
  /** the engine's own words (an error code line), for whoever wants the technical detail */
  detail: string | null;
  failedStep: string | null;
  /** the item's own blocked mark, said with names */
  mark: string | null;
  run: {
    id: string;
    label: string;
    url: string | null;
    status: string | null;
    at: string | null;
  } | null;
}

export interface TodoWorkspace {
  teamId: string;
  name: string;
  current: boolean;
}

/** How a history line reads at a glance — a failure must LOOK like one (owner 2026-10-02). */
export type HistoryTone =
  | "done"
  | "recovered"
  | "failed"
  | "waiting"
  | "working"
  | "cancelled"
  | "idle";

/** One line of a card's "What happened": a step of the subject (or a question on it), oldest first. */
export interface TodoHistoryLine {
  key: string;
  title: string;
  kind: "step" | "question";
  who: string | null;
  outcome: string;
  done: boolean;
  at: string | null;
  report: string | null;
  /** The colour the line is read in; absent on an older server (then `done` decides). */
  tone?: HistoryTone;
  /** How many identical lines this one stands for (4 auto-answered copies = one line, "4×"). */
  times?: number;
  /** Attempts of this step that failed before it ended the way it did. */
  attemptsFailed?: number;
  /** An open question addressed to the caller: it carries its own answer buttons. */
  needsYou?: boolean;
  /** That question's own to-do entry id (`q:<id>`), when it has one. */
  entryId?: string | null;
  question?: {
    key: string;
    text: string | null;
    questionClass: string | null;
    answerType: string | null;
    /** The agent's report made readable (server, 2026-10-15); absent on an older server — then `text` is shown. */
    readable?: TodoReadable | null;
  } | null;
  actions?: TodoAction[] | null;
}

/** One numbered check of a step, by the name its prompt gave it, and how the agent's report says it came out. */
export interface TodoCheck {
  n: number;
  text: string;
  status: "pass" | "fail" | "unknown";
}

/**
 * Owner 2026-10-15: "criteria 1 2 and so on … does not say anything to the user". What an agent's stopped step
 * reported, for a person: a one-line headline, the step's checks by NAME, what the agent asks for, the full report.
 */
export interface TodoReadable {
  headline: string | null;
  stepName: string | null;
  checks: { items: TodoCheck[]; passed: number; failed: number; total: number } | null;
  ask: string | null;
  report: string | null;
}

/** A run a card's text mentions, named by the server so it is shown — and linked — as its name, never its id. */
export interface TodoRef {
  kind: "run";
  label: string;
  url: string | null;
  teamId: string;
  status: string | null;
  at: string | null;
}

/** What is being asked of the person — the three buckets the summary strip counts. */
export type TodoGroup = "stopped" | "decide" | "elsewhere";

/** 23 §postpone — one call, a few plain spans. */
export interface TodoPostpone {
  call: TodoCall;
  options: Array<{ id: string; label: string; hours: number }>;
}

/** One side of a blocking relation, with the call that takes it off again. */
export interface TodoBlockingLink {
  key: string;
  title: string;
  state: string;
  open: boolean;
  url: string;
  remove?: { key: string; type: string; targetKey: string };
}

/** 23 §blocking — what holds the subject, what it holds, and the endpoints that change either side. */
export interface TodoBlocking {
  key: string;
  canEdit: boolean;
  blockedBy: TodoBlockingLink[];
  blocks: TodoBlockingLink[];
  search: TodoCall;
  link: TodoCall;
  unlink: TodoCall;
}

/** A card this person put off: listed, never lost. */
export interface TodoPostponed {
  id: string;
  kind: TodoKind;
  title: string;
  until: string;
  workspace: TodoWorkspace;
}

export interface TodoEntry {
  id: string;
  kind: TodoKind;
  /** The server's bucket for the summary strip; absent on an older server (then `groupOf(kind)`). */
  group?: TodoGroup;
  title: string;
  explanation: string;
  mine: boolean;
  impact: { unblocksCount: number; keys: string[]; chain: TodoChainLink[] };
  urgency: "critical" | "high" | "normal";
  since: string;
  ageSeconds: number;
  dueAt: string | null;
  workspace: TodoWorkspace;
  project: string | null;
  /** The subject's steps and answered questions, so a person checks instead of guessing (server, 2026-09-29). */
  history?: TodoHistoryLine[];
  /** Every run id the card's words mention, by lower-case id → its name and link (server, 2026-10-15). */
  refs?: Record<string, TodoRef> | null;
  /** Why this card is here, in words (server, 2026-10-16: "a lot of blocks and no info about why"). */
  why?: TodoWhyInfo | null;
  /** A to-do from the person's mail (server, 2026-10-20): the mail it came from, and the tile it was filed as. */
  mail?: {
    todoId: string;
    itemId: string;
    subject: string | null;
    from: string | null;
    receivedAt: string | null;
    why: string | null;
    dueAt: string | null;
    workItemKey: string | null;
    url: string;
    tileUrl: string | null;
    canFile: boolean;
  } | null;
  /** BRIEF-CGNI slice 4: the channel reading a `channelDecision` card stands on. */
  reading?: {
    readingId: string;
    title: string;
    meaning: string | null;
    importance: string;
    importanceWhy: string | null;
    originKind: string;
    consumer: "workspace" | "project";
    consumerId: string;
    href: string | null;
    dueAt: string | null;
  } | null;
  item: {
    id: string;
    key: string;
    title: string;
    kind: string;
    state: string;
    blocked: boolean;
    blockedReason: string | null;
    url: string;
    /** The item's first prose paragraph — what it IS, for a person deciding (server, 2026-09-29). */
    summary?: string | null;
    /** Its acceptance checklist — what "done" means for it. */
    acceptance?: string[];
  } | null;
  question: {
    id: string;
    key: string;
    title: string;
    text: string | null;
    questionClass: string | null;
    answerType: string | null;
    url: string;
  } | null;
  step: Record<string, unknown> | null;
  plan: {
    planRef: string;
    projectKey: string | null;
    proposed: number;
    total: number;
    url: string;
  } | null;
  leftover: Array<{ key: string; title: string; blockedReason: string | null }> | null;
  postpone?: TodoPostpone | null;
  blocking?: TodoBlocking | null;
  evidence: {
    step?: string | null;
    result?: string | null;
    files?: string | null;
    evidence?: string | null;
    body?: string | null;
  } | null;
  actions: TodoAction[];
}

export interface WorkTodoData {
  ok: boolean;
  at: string;
  scope: string;
  projectKey: string | null;
  workspace: TodoWorkspace;
  workspaces: Array<TodoWorkspace & { count: number }>;
  entries: TodoEntry[];
  postponed?: TodoPostponed[];
  counts: {
    total: number;
    byKind: Partial<Record<TodoKind, number>>;
    byGroup?: Partial<Record<TodoGroup, number>>;
    unblocks: number;
    mine: number;
    postponed?: number;
  };
}

const URGENCY: Record<string, number> = { critical: 0, high: 1, normal: 2 };

/** The server's order, re-applied client-side after an optimistic change: impact ↓, urgency, oldest first, kind. */
export function sortEntries(entries: TodoEntry[]): TodoEntry[] {
  return [...entries].sort(
    (a, b) =>
      (b.impact?.unblocksCount ?? 0) - (a.impact?.unblocksCount ?? 0) ||
      (URGENCY[a.urgency] ?? 2) - (URGENCY[b.urgency] ?? 2) ||
      (b.ageSeconds ?? 0) - (a.ageSeconds ?? 0) ||
      TODO_KINDS.indexOf(a.kind) - TODO_KINDS.indexOf(b.kind) ||
      a.id.localeCompare(b.id),
  );
}

/** Kind groups in kind order, for the summary chips (only kinds that occur). */
export function groupByKind(
  entries: TodoEntry[] | null | undefined,
): Array<{ kind: TodoKind; count: number }> {
  const counts = new Map<TodoKind, number>();
  for (const e of entries ?? []) counts.set(e.kind, (counts.get(e.kind) ?? 0) + 1);
  return TODO_KINDS.filter((k) => counts.has(k)).map((k) => ({
    kind: k,
    count: counts.get(k) ?? 0,
  }));
}

/** A copy of `body` with `text` written at the dotted `path` (objects on the way are created). */
export function setAtPath(
  body: Record<string, unknown>,
  path: string,
  text: string,
): Record<string, unknown> {
  const out = structuredClone(body);
  const parts = path.split(".");
  let node: Record<string, unknown> = out;
  for (const p of parts.slice(0, -1)) {
    const next = node[p];
    if (!next || typeof next !== "object") node[p] = {};
    node = node[p] as Record<string, unknown>;
  }
  node[parts[parts.length - 1] as string] = text;
  return out;
}

/** `/api/work.questions.answer` → `work.questions.answer` (the adapter's `call(method, body)`). */
export function methodOf(call: TodoCall): string {
  return call.path.replace(/^\/api\//, "");
}

/** The calls an action makes, in order, each with the text placed where its `textPath` says (blank text is not sent). */
export function actionCalls(
  action: TodoAction,
  text: string,
): Array<{ method: string; body: Record<string, unknown> }> {
  const calls = [action.call, ...(action.andThen ?? [])].filter((c): c is TodoCall => Boolean(c));
  const trimmed = text.trim();
  return calls.map((c) => ({
    method: methodOf(c),
    body: c.textPath && trimmed ? setAtPath(c.body, c.textPath, trimmed) : structuredClone(c.body),
  }));
}

/**
 * The CancelDialog request of a preview-apply action, naming the entry's workspace when it is not the current one
 * (23 §cross-workspace): the server stamps `cancel.teamId`; an entry of another workspace without one gets its own.
 */
export function cancelRequestFor(entry: TodoEntry, action: TodoAction): CancelRequest | null {
  if (!action.cancel) return null;
  if (entry.workspace.current) return action.cancel;
  return { ...action.cancel, teamId: action.cancel.teamId ?? entry.workspace.teamId };
}

/** Whether the action can be pressed with this text: required text must be there; open actions never "run". */
export function canRun(action: TodoAction, text: string): boolean {
  if (action.kind === "open") return false;
  if (action.requiresText && !text.trim()) return false;
  return action.call !== null;
}

/** Whether pressing the action first needs a text box / a confirmation step (never one click for danger or text). */
export function needsStep(action: TodoAction): boolean {
  return (
    action.kind === "call" && (action.requiresText || Boolean(action.acceptsText) || action.danger)
  );
}

/** The data after an action succeeded on `entryId`: the entry gone, counts adjusted — the refetch then tells the truth. */
export function removeEntry(
  data: WorkTodoData | undefined,
  entryId: string,
): WorkTodoData | undefined {
  if (!data) return data;
  const gone = data.entries.find((e) => e.id === entryId);
  if (!gone) return data;
  const entries = data.entries.filter((e) => e.id !== entryId);
  const byKind = { ...data.counts.byKind };
  const n = (byKind[gone.kind] ?? 1) - 1;
  if (n > 0) byKind[gone.kind] = n;
  else delete byKind[gone.kind];
  return {
    ...data,
    entries,
    counts: {
      ...data.counts,
      total: Math.max(0, data.counts.total - 1),
      byKind,
      mine: gone.mine ? Math.max(0, data.counts.mine - 1) : data.counts.mine,
    },
    workspaces: data.workspaces.map((w) =>
      w.teamId === gone.workspace.teamId ? { ...w, count: Math.max(0, w.count - 1) } : w,
    ),
  };
}

/** What clearing the entry unblocks: the keys the server listed (nearest first). */
export function unblockedKeys(entry: TodoEntry): string[] {
  return entry.impact?.keys ?? [];
}

/** A VS Code deep link that opens the repo folder (the queued step starts there by itself). */
export function vscodeFolderLink(repoPath: string | null | undefined): string | null {
  if (!repoPath) return null;
  return `vscode://file${repoPath.startsWith("/") ? "" : "/"}${encodeURI(repoPath)}`;
}

/** Filter by kind (null = all). */
export function filterEntries(
  entries: TodoEntry[] | undefined,
  kind: TodoKind | null,
): TodoEntry[] {
  return (entries ?? []).filter((e) => kind === null || e.kind === kind);
}

// ── 2026-10-02: made readable for a person who did not write the flow ────────────────────────────
// Owner, on the live page: "note how unexplanatory the problem shows — what is supposed to do the
// user? … show failures in a reddish background to draw the eye to where the problem is …
// summarize the problems on top with easy choices where possible … postponing should also be
// possible — blockings on this and from this should also be possible." The rules below are pure;
// the scene draws them.

/** The server's bucket, mirrored so an older server (no `group`) still groups the same way. */
export function groupOf(kind: TodoKind): TodoGroup {
  switch (kind) {
    case "stop":
    case "failedStep":
    case "flowStalled":
    case "leftover":
    case "manualBlock":
      return "stopped";
    case "acceptance":
    case "question":
    case "planApproval":
    case "personStep":
    case "mailTodo":
    case "channelDecision":
      return "decide";
    default:
      return "elsewhere";
  }
}

export function groupFor(entry: TodoEntry): TodoGroup {
  return entry.group ?? groupOf(entry.kind);
}

export const TODO_GROUPS: TodoGroup[] = ["stopped", "decide", "elsewhere"];

/** The summary strip: the three buckets that actually occur, in order, with their counts. */
export function summarize(
  entries: TodoEntry[] | null | undefined,
): Array<{ group: TodoGroup; count: number }> {
  const counts = new Map<TodoGroup, number>();
  for (const e of entries ?? []) {
    const g = groupFor(e);
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  return TODO_GROUPS.filter((g) => counts.has(g)).map((g) => ({
    group: g,
    count: counts.get(g) ?? 0,
  }));
}

export function filterByGroup(
  entries: TodoEntry[] | undefined,
  group: TodoGroup | null,
): TodoEntry[] {
  return (entries ?? []).filter((e) => group === null || groupFor(e) === group);
}

/** The tone of a history line, with the fallback an older server needs. */
export function toneOf(line: TodoHistoryLine): HistoryTone {
  if (line.tone) return line.tone;
  if (line.kind === "question") return line.done ? "done" : "waiting";
  return line.done ? "done" : "working";
}

/** Whether a line is a place the eye should go: it failed, or it still waits on a person. */
export function isTrouble(line: TodoHistoryLine): boolean {
  const tone = toneOf(line);
  return tone === "failed" || (line.attemptsFailed ?? 0) > 0;
}

/**
 * The questions INSIDE a card that still want an answer from this person. They are what the person
 * came for, so the card shows them first — not folded into the story at the bottom.
 */
export function answerable(entry: TodoEntry): TodoHistoryLine[] {
  return (entry.history ?? []).filter((h) => h.needsYou === true && (h.actions?.length ?? 0) > 0);
}

/** The call that puts a card off for `hours`, or (hours null) brings it back now. */
export function postponeCall(
  entry: TodoEntry,
  hours: number | null,
): { method: string; body: Record<string, unknown> } | null {
  const call = entry.postpone?.call;
  if (!call) return null;
  const body = structuredClone(call.body);
  if (hours === null) body.cancel = true;
  else body.hours = hours;
  return { method: methodOf(call), body };
}

/** The call that brings a postponed card back, from the folded list (which carries no `postpone`). */
export function unpostponeCall(row: TodoPostponed): {
  method: string;
  body: Record<string, unknown>;
} {
  return {
    method: "work.todo.postpone",
    body: { entryId: row.id, teamId: row.workspace.teamId, cancel: true },
  };
}

/** `blocked_by` / `blocks` on the subject of a card, as a call the surface can make. */
export function linkCall(
  blocking: TodoBlocking,
  side: "blockedBy" | "blocks",
  targetKey: string,
): { method: string; body: Record<string, unknown> } {
  const body = structuredClone(blocking.link.body);
  body.key = blocking.key;
  body.type = side === "blockedBy" ? "blocked_by" : "blocks";
  body.target = { kind: "item", key: targetKey };
  return { method: methodOf(blocking.link), body };
}

/** Taking one off again: the call must name the side the stored row is on (`remove`). */
export function unlinkCall(
  blocking: TodoBlocking,
  link: TodoBlockingLink,
): { method: string; body: Record<string, unknown> } | null {
  if (!link.remove) return null;
  const body = structuredClone(blocking.unlink.body);
  body.key = link.remove.key;
  body.type = link.remove.type;
  body.target = { kind: "item", key: link.remove.targetKey };
  return { method: methodOf(blocking.unlink), body };
}

/** A span in words ("in 4 hours", "tomorrow") — what a postponed card says it is waiting for. */
export function untilWords(until: string | null | undefined, now = Date.now()): string {
  if (!until) return "";
  const ms = new Date(until).getTime() - now;
  if (Number.isNaN(ms)) return "";
  if (ms <= 0) return "now";
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return "in under an hour";
  if (hours < 24) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return days === 1 ? "tomorrow" : `in ${days} days`;
}
